import { useMemo } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { BarChart3 } from "lucide-react";

const COLORS = [
  "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];

const fmt = (v) => {
  if (typeof v !== "number") return v;
  if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toLocaleString();
};

const InlineChart = ({ chartData }) => {
  const { type, title, data, xKey = "name", yKeys = [] } = chartData;
  if (!data || data.length === 0) return null;

  // Normalize yKeys to array of objects
  const keys = yKeys.map((k, i) =>
    typeof k === "string" ? { key: k, label: k, color: COLORS[i % COLORS.length] } : { color: COLORS[i % COLORS.length], ...k }
  );

  // If no yKeys provided, auto-detect numeric fields
  if (keys.length === 0) {
    const sample = data[0];
    Object.keys(sample).forEach((k, i) => {
      if (k !== xKey && k !== "name" && typeof sample[k] === "number") {
        keys.push({ key: k, label: k, color: COLORS[i % COLORS.length] });
      }
    });
  }

  return (
    <div className="my-3 p-3 bg-background border rounded-lg" data-testid="inline-chart">
      {title && (
        <div className="flex items-center gap-1.5 mb-2">
          <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={220}>
        {type === "pie" || type === "donut" ? (
          <PieChart>
            <Pie
              data={data}
              cx="50%" cy="50%"
              innerRadius={type === "donut" ? 50 : 0}
              outerRadius={80}
              dataKey={keys[0]?.key || "value"}
              nameKey={xKey}
              label={({ name, value }) => `${name}: ${fmt(value)}`}
              labelLine={false}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
          </PieChart>
        ) : type === "line" ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} />
            {keys.length > 1 && <Legend />}
            {keys.map((k) => (
              <Line
                key={k.key} type="monotone" dataKey={k.key}
                stroke={k.color} name={k.label}
                strokeWidth={2} dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} />
            {keys.length > 1 && <Legend />}
            {keys.map((k) => (
              <Bar key={k.key} dataKey={k.key} fill={k.color} name={k.label} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const CHART_REGEX = /\[CHART\]([\s\S]*?)\[\/CHART\]/g;

const ChatMessageRenderer = ({ content }) => {
  const parts = useMemo(() => {
    if (!content) return [{ type: "text", value: "" }];
    const result = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(CHART_REGEX.source, "g");

    while ((match = regex.exec(content)) !== null) {
      // Text before chart
      if (match.index > lastIndex) {
        result.push({ type: "text", value: content.slice(lastIndex, match.index) });
      }
      // Parse chart JSON
      try {
        const chartData = JSON.parse(match[1].trim());
        result.push({ type: "chart", value: chartData });
      } catch {
        // If JSON parse fails, keep as text
        result.push({ type: "text", value: match[0] });
      }
      lastIndex = match.index + match[0].length;
    }

    // Remaining text
    if (lastIndex < content.length) {
      result.push({ type: "text", value: content.slice(lastIndex) });
    }

    return result.length ? result : [{ type: "text", value: content }];
  }, [content]);

  return (
    <div className="text-sm leading-relaxed">
      {parts.map((part, i) =>
        part.type === "chart" ? (
          <InlineChart key={i} chartData={part.value} />
        ) : (
          <div key={i} className="whitespace-pre-wrap">{part.value.split(/(\*\*[^*]+\*\*)/g).map((text, j) => text.startsWith("**") && text.endsWith("**") ? <strong key={j}>{text.slice(2, -2)}</strong> : text)}</div>
        )
      )}
    </div>
  );
};

export default ChatMessageRenderer;
